import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../lib/cn";

interface Option<T extends string> {
  id: T;
  label: string;
  icon?: React.ReactNode;
  /** When true, the option is rendered greyed-out and is non-interactive.
   *  The thumb still tracks the currently-active option, so disabling a
   *  non-active option keeps the visual selection stable. */
  disabled?: boolean;
  /** Optional tooltip (native `title` attribute) — useful for explaining
   *  why a `disabled` option can't be picked. */
  title?: string;
}

interface Props<T extends string> {
  options: Option<T>[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
  /** `touch` = compact labels at a 44px height, for phone toolbars. */
  size?: "sm" | "md" | "touch";
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  className,
  size = "md",
}: Props<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState({ x: 0, w: 0 });

  useLayoutEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const active = c.querySelector<HTMLButtonElement>(`button[data-active="true"]`);
    if (!active) return;
    const cRect = c.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    setThumb({ x: aRect.left - cRect.left, w: aRect.width });
  }, [value, options.length]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative inline-flex items-center rounded-2xl bg-ink-100 p-1 ring-1 ring-ink-200/60",
        className
      )}
    >
      <div
        aria-hidden
        className="absolute top-1 bottom-1 rounded-xl bg-white shadow-sm segmented-thumb"
        style={{ transform: `translateX(${thumb.x - 4}px)`, width: thumb.w }}
      />
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          data-active={value === o.id}
          disabled={o.disabled}
          aria-disabled={o.disabled || undefined}
          title={o.title}
          onClick={() => onChange(o.id)}
          className={cn(
            "relative z-10 inline-flex items-center justify-center gap-1.5 rounded-xl font-medium transition-colors",
            // Bumped to h-9 (36px) on mobile + sm:h-8 (32px) on desktop so
            // mobile finger taps land cleanly. Horizontal padding starts
            // tight (px-2.5) on the smallest phones (e.g. 320px iPhone 5)
            // and grows on sm so the top-bar cluster never overlaps the
            // segmented chips.
            size === "sm"
              ? "h-9 sm:h-8 pointer-coarse:h-11 min-w-9 px-2.5 sm:px-3 text-xs"
              : size === "touch"
                ? "h-10 min-w-10 px-3 text-body-sm"
                : "h-11 sm:h-10 px-4 text-sm",
            value === o.id ? "text-ink-900" : "text-ink-500 hover:text-ink-700",
            o.disabled && "opacity-40 cursor-not-allowed hover:text-ink-500"
          )}
        >
          {o.icon}
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}
